import React from 'react';
import { GenericDetailPage } from '../../../components/generic';
import { NOON_ACTIVITY_LABELS } from './constants';
import { moduleConfig } from './config';
import type { NoonReport } from './types';
import { useNoonReport } from './useData';

const displayDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : '—';

const getDetailSections = (
  item: NoonReport,
  formatDate: (value?: string) => string,
) => {
  const fmt = moduleConfig.utils.formatNumber;
  const activities = (item.activities || [])
    .map((activity) => NOON_ACTIVITY_LABELS[activity] || activity)
    .join(', ');

  return [
    {
      title: 'Overview',
      fields: [
        { label: 'Vessel', value: moduleConfig.utils.vesselLabel(item) },
        {
          label: 'Report date',
          value: formatDate(String(item.reportDate).slice(0, 10)),
        },
        { label: 'Time', value: item.reportTime || '—' },
        { label: 'GMT', value: item.gmtOffset || '—' },
        { label: 'Condition', value: item.condition || '—' },
        { label: 'Activities', value: activities || '—' },
      ],
    },
    {
      title: 'Position & cargo',
      fields: [
        { label: 'Latitude', value: item.latitude || '—' },
        { label: 'Longitude', value: item.longitude || '—' },
        { label: 'ETA', value: displayDateTime(item.eta) },
        { label: 'ETD', value: displayDateTime(item.etd) },
        { label: 'Next port', value: item.nextPort || '—' },
        { label: 'Distance covered (NM)', value: fmt(item.distanceCovered) },
        { label: 'Distance to go (NM)', value: fmt(item.distanceToGo) },
        { label: 'Cargo type', value: item.cargoType || '—' },
        { label: 'Cargo on board (MT)', value: fmt(item.cargoOnBoardMt, 3) },
      ],
    },
    {
      title: 'Draft & speed',
      fields: [
        { label: 'Draft FWD (m)', value: fmt(item.draftFwd) },
        { label: 'Draft MID (m)', value: fmt(item.draftMid) },
        { label: 'Draft AFT (m)', value: fmt(item.draftAft) },
        { label: 'Present speed (kn)', value: fmt(item.presentSpeed) },
        { label: 'Avg speed 24h (kn)', value: fmt(item.avgSpeed24h) },
        { label: 'Avg RPM', value: fmt(item.avgRpm, 1) },
        { label: 'Steaming hours', value: fmt(item.steamingHours) },
        { label: 'Pitch (mm)', value: fmt(item.pitchMm, 1) },
        { label: 'Avg SOG (kn)', value: fmt(item.avgSog) },
        { label: 'Avg STW (kn)', value: fmt(item.avgStw) },
        { label: 'Theoretical speed (kn)', value: fmt(item.theoreticalSpeed, 3) },
        { label: 'Slip (%)', value: fmt(item.slipPercent) },
      ],
    },
    {
      title: 'Weather',
      fields: [
        { label: 'Sea state', value: item.seaState?.name || '—' },
        {
          label: 'Sea current',
          value:
            [item.seaCurrentDirection?.name, item.seaCurrentForce]
              .filter(Boolean)
              .join(' / ') || '—',
        },
        {
          label: 'Wind',
          value:
            [item.windFrom?.name, item.windForce].filter(Boolean).join(' / ') ||
            '—',
        },
        {
          label: 'Swell',
          value:
            [item.swellDirection?.name, item.swellForce]
              .filter(Boolean)
              .join(' / ') || '—',
        },
        { label: 'Weather notes', value: item.weatherNotes || '—' },
        { label: 'Notes', value: item.notes || '—' },
      ],
    },
    {
      title: 'ROB',
      fields: [
        { label: 'FO (MT)', value: fmt(item.robFoMt, 3) },
        { label: 'MGO/MDO (MT)', value: fmt(item.robMgoMdoMt, 3) },
        { label: 'System oil (L)', value: fmt(item.robSystemOilL) },
        { label: 'Cylinder oil (L)', value: fmt(item.robCylinderOilL) },
        { label: 'D/G system oil (L)', value: fmt(item.robDgSystemOilL) },
        { label: 'Hydr. oil (L)', value: fmt(item.robHydrOilL) },
        { label: 'FW (m³)', value: fmt(item.robFwM3, 3) },
        { label: 'Sludges (m³)', value: fmt(item.robSludgesM3, 3) },
        { label: 'Bilges (m³)', value: fmt(item.robBilgesM3, 3) },
      ],
    },
    {
      title: 'Consumption',
      fields: [
        { label: 'M/E FO (MT)', value: fmt(item.consMeFoMt, 3) },
        { label: 'D/G FO (MT)', value: fmt(item.consDgFoMt, 3) },
        { label: 'BLR FO (MT)', value: fmt(item.consBlrFoMt, 3) },
        { label: 'IGG FO (MT)', value: fmt(item.consIggFoMt, 3) },
        { label: 'M/E MGO/MDO (MT)', value: fmt(item.consMeMgoMdoMt, 3) },
        { label: 'D/G MGO/MDO (MT)', value: fmt(item.consDgMgoMdoMt, 3) },
        { label: 'BLR MGO/MDO (MT)', value: fmt(item.consBlrMgoMdoMt, 3) },
        { label: 'IGG MGO/MDO (MT)', value: fmt(item.consIggMgoMdoMt, 3) },
        {
          label: 'Incinerator MGO/MDO (MT)',
          value: fmt(item.consIncineratorMgoMdoMt, 3),
        },
        { label: 'System oil (L)', value: fmt(item.consSystemOilL) },
        { label: 'Cylinder oil (L)', value: fmt(item.consCylinderOilL) },
        { label: 'D/G system oil (L)', value: fmt(item.consDgSystemOilL) },
        { label: 'Hydr. oil (L)', value: fmt(item.consHydrOilL) },
        { label: 'FW (m³)', value: fmt(item.consFwM3, 3) },
      ],
    },
    {
      title: 'Running hours',
      fields: [
        ['Main engine', item.rhMeTotal, item.rhMeDaily],
        ['Aux 1', item.rhAux1Total, item.rhAux1Daily],
        ['Aux 2', item.rhAux2Total, item.rhAux2Daily],
        ['Aux 3', item.rhAux3Total, item.rhAux3Daily],
        ['Emergency aux', item.rhEmergencyAuxTotal, item.rhEmergencyAuxDaily],
        ['Boiler', item.rhBoilerTotal, item.rhBoilerDaily],
        ['IGG', item.rhIggTotal, item.rhIggDaily],
        ['Incinerator', item.rhIncineratorTotal, item.rhIncineratorDaily],
      ].map(([label, total, daily]) => ({
        label: String(label),
        value: `Total ${fmt(total as number | string | null)} / Daily ${fmt(
          daily as number | string | null,
        )}`,
      })),
    },
  ];
};

const Detail: React.FC = () => (
  <GenericDetailPage<NoonReport>
    useData={useNoonReport}
    config={{
      ...moduleConfig,
      getDetailSections,
      getStatusBadges: () => null,
    }}
  />
);

export default Detail;
